import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Modal } from "react-bootstrap";
import Breadcrumbs from "../../../../shared/components/Breadcrumbs";
import { EMAIL_EXECUTION } from "../../../../shared/constants/Constants";
import ServerSideGrid from "../../../../shared/components/listview/ServerSideGrid";
import { loadTicketEmailExecutionLogGetDataRequest } from "../../../ticketing/ticketing/redux/Actions";
import CustomSelectFilter from "../../../../shared/components/listview/CustomSelectFilter";

const FIELD_LABELS = {
  email_from:   "From",
  email_to:     "To",
  email_subject:"Subject",
  email_body:   "Body",
  email_cc:     "CC",
  sender_email: "Sender Email",
  subject:      "Subject",
};

const OPERATOR_LABELS = {
  contains:     "contains",
  not_contains: "does not contain",
  equals:       "equals",
  not_equals:   "does not equal",
  starts_with:  "starts with",
  ends_with:    "ends with",
  is_empty:     "is empty",
  is_not_empty: "is not empty",
  in:           "contains",
};

const TicketEmailExec = () => {
  const dispatch = useDispatch();
  const { emailExecutionLogs, loading } = useSelector((state) => state.ticketing);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const [externalGridFilter] = React.useState({
    filterModel: {
      execution_result: {
        filterType: "text",
        type: "equals",        
        filter: "failed",      
      },
    },
    sortModel: [],
    columnVisibility: null,
    columnOrder: [],
  });

  useEffect(() => {
    dispatch(loadTicketEmailExecutionLogGetDataRequest());
  }, [dispatch]);

  const parsedConditions = useMemo(() => {
    if (!selectedEmail?.matched_conditions) return null;
    try {
      const mc = typeof selectedEmail.matched_conditions === "string"
        ? JSON.parse(selectedEmail.matched_conditions)
        : selectedEmail.matched_conditions;
      return mc?.groups?.flatMap(group =>
        group.conditions.map(cond => ({ ...cond, logic: group.logic }))
      ) ?? null;
    } catch {
      return null;
    }
  }, [selectedEmail?.matched_conditions]);

  const columnDefs = useMemo(() => [
    { headerName: "ID",               field: "id",                    width: 70,  hide: true },
    {
      headerName: "Ticket ID",
      field: "ticket_number",
      width: 140,
      pinned: true,
      cellRenderer: (params) =>
        params.data?.ticket_id ? (
          <a href={`/ticketing/${params.data.ticket_id}`}>{params.value}</a>
        ) : params.value,
    },
    {
      headerName: "Email Rule",
      field: "rule_name",
      width: 120,
      cellRenderer: (params) =>
        params.data?.email_rule_id ? (
          <a href={`/emailruleform/${params.data.email_rule_id}`}>{params.value}</a>
        ) : params.value,
    },
    {
      headerName: "Email Subject",
      field: "email_subject",
      width: 250,
      type: "customLink",
      functionTo: (data) => setSelectedEmail(data),
    },
    { headerName: "From",              field: "email_from",            width: 280 },
    { headerName: "To",                field: "email_to",              width: 280 },
    { 
      headerName: "Result",
      field: "execution_result",
      width: 130,
      filter: "agTextColumnFilter",   
      filterParams: {
        filterOptions: ['equals', 'contains'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
      },
    },
    {
      headerName: "Workflow ID",
      field: "workflow_number",
      width: 140,
      cellRenderer: (params) =>
        params.data?.workflow_id ? (
          <a href={`/ticketing/${params.data.workflow_id}`}>{params.value}</a>
        ) : params.value,
    },
    { headerName: "Error Message",     field: "error_message",         width: 200 },
    { headerName: "Executed On",       field: "executed_on_formatted", width: 200, filter: "agDateColumnFilter" },
    { headerName: "Email Received At", field: "email_received_at",     width: 200, filter: "agDateColumnFilter" },
  ], []);

  const isSuccess = selectedEmail?.execution_result?.toLowerCase() === "executed";

  const metaRow = {
    display: "flex",
    gap: 8,
    padding: "7px 0",
    borderBottom: "1px solid #ebebeb",
    fontSize: 13,
  };
  const metaLabel = { width: 120, color: "#888", flexShrink: 0, fontWeight: 600 };
  const metaValue = { color: "#222", wordBreak: "break-word" };

  return (
    <>
      <Row className="detailsPageHeader">
        <Col>
          <Breadcrumbs children={[{ href: "/ticket-email-execution/", label: EMAIL_EXECUTION }]} />
        </Col>
      </Row>

      <Row>
        <Card className="table-card">
          <Card.Body>
            <ServerSideGrid
              clientSide={true}
              columnDefs={columnDefs}
              rowData={emailExecutionLogs}
              loading={loading}
              paginationPageSize={80}
              defaultColDef={{ filter: "agTextColumnFilter" }}
              externalFilter={externalGridFilter}  

            />
          </Card.Body>
        </Card>
      </Row>

      <Modal show={!!selectedEmail} onHide={() => setSelectedEmail(null)} size="lg" centered>

            {/* ── Header ── */}
            <Modal.Header closeButton style={{ background: "#f5f5f5", borderBottom: "1px solid #ddd", padding: "12px 20px" }}>
              <div style={{ width: "100%" }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a", marginBottom: 6 }}>
                  {selectedEmail?.email_subject || "—"}
                </div>
                <span style={{
                  display: "inline-block",
                  fontSize: 11, fontWeight: 700,
                  padding: "2px 10px", borderRadius: 20,
                  background: isSuccess ? "#e6f4ea" : "#fdecea",
                  color: isSuccess ? "#2e7d32" : "#c62828",
                  border: `1px solid ${isSuccess ? "#a8d5b0" : "#f5b8b5"}`,
                  letterSpacing: "0.4px",
                }}>
                  {selectedEmail?.execution_result?.toUpperCase() || "—"}
                </span>
              </div>
            </Modal.Header>

            {/* ── Scrollable Body ── */}
            <Modal.Body style={{ padding: 0, maxHeight: "70vh", overflowY: "auto" }}>

              {/* ── Email meta ── */}
              <div style={{ padding: "12px 20px", borderBottom: "2px solid #ebebeb", background: "#fafafa" }}>
                <div style={metaRow}>
                  <span style={metaLabel}>From</span>
                  <span style={metaValue}>{selectedEmail?.email_from || "—"}</span>
                </div>
                <div style={metaRow}>
                  <span style={metaLabel}>To</span>
                  <span style={metaValue}>{selectedEmail?.email_to || "—"}</span>
                </div>
                <div style={{ ...metaRow, borderBottom: "none" }}>
                  <span style={metaLabel}>Date</span>
                  <span style={metaValue}>
                    {selectedEmail?.email_received_at
                      ? new Date(selectedEmail.email_received_at).toLocaleString()
                      : "—"}
                  </span>
                </div>
              </div>

              {/* ── Email body ── */}
              <div style={{ padding: "16px 20px", borderBottom: "2px solid #ebebeb" }}>
                <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                  <span style={metaLabel}>Body</span>
                  <span style={{ color: "#222", lineHeight: 1.7, wordBreak: "break-word", flex: 1 }}>
                    {selectedEmail?.email_body_preview ? (
                      <>
                        <style>{`.email-body-content img[src^="cid:"] { display: none; }`}</style>
                        <div
                          className="email-body-content"
                          dangerouslySetInnerHTML={{
                            __html: selectedEmail.email_body_preview
                              .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')  // strip style tags
                              .replace(/<a[^>]*>.*?Report This Email.*?<\/a>/gis, '')
                          }}
                        />
                      </>
                    ) : (
                      <span style={{ color: "#999" }}>No body content.</span>
                    )}
                  </span>
                </div>
              </div>

              {/* ── Email Rule Section ── */}
              <div style={{ borderBottom: "2px solid #ebebeb" }}>

                {/* Section header bar */}
                <div style={{
                  padding: "8px 20px",
                  background: "#e8e8e8",
                  borderBottom: "1px solid #d0d0d0",
                  fontSize: 11, fontWeight: 700,
                  color: "#555",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}>
                  Email Rule
                </div>

                <div style={{ padding: "12px 20px", background: "#fafafa" }}>

                  {/* Rule name */}
                  <div style={{ ...metaRow }}>
                    <span style={metaLabel}>Name</span>
                    <span style={{ color: "#1a1a1a", fontWeight: 700, fontSize: 14 }}>
                      {selectedEmail?.rule_name || "—"}
                    </span>
                  </div>

                  {/* Conditions */}
                  <div style={{ ...metaRow, borderBottom: "none" }}>
                    <span style={metaLabel}>Conditions</span>
                    <span style={{ color: "#333", lineHeight: 1.6, fontSize: 13 }}>
                      {parsedConditions ? parsedConditions.map((cond, i) => {
                        const field    = FIELD_LABELS[cond.field]       || cond.field    || "Field";
                        const operator = OPERATOR_LABELS[cond.operator] || cond.operator || "matches";
                        const value    = cond.value ?? "";
                        const logic    = cond.logic || "AND";
                        return (
                          <span key={i}>
                            {i > 0 && (
                              <span style={{ fontWeight: 700, margin: "0 6px", fontSize: 11, color: "#888" }}>
                                {logic}
                              </span>
                            )}
                            <span style={{ fontWeight: 600 }}>{field}</span>
                            {" "}
                            <span>{operator}</span>
                            {value && <span> "{value}"</span>}
                          </span>
                        );
                      }) : <span style={{ color: "#999" }}>No conditions available.</span>}
                    </span>
                  </div>

                </div>
              </div>

              {/* ── Error message ── */}
              {selectedEmail?.error_message && (
                <div style={{ borderBottom: "2px solid #ebebeb" }}>
                  <div style={{
                    padding: "8px 20px",
                    background: "#e8e8e8",
                    borderBottom: "1px solid #d0d0d0",
                    fontSize: 11, fontWeight: 700,
                    color: "#555",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}>
                    Error
                  </div>
                  <div style={{ padding: "12px 20px", background: "#fff8f8" }}>
                    <p style={{ margin: 0, fontSize: 13, color: "#c62828" }}>
                      {selectedEmail.error_message}
                    </p>
                  </div>
                </div>
              )}

            </Modal.Body>
          </Modal>
    </>
  );
};

export default TicketEmailExec;